/**
 * Pure decision helper for the cold-load session bootstrap. Distinguishes a
 * genuine "no session" (route to /login) from a transient getSession/me failure
 * (hold and let the retrying query recover) so a network/5xx blip on app load
 * never bounces a validly-cookied user to /login. Extracted from the App
 * bootstrap effect so the decision is unit-testable without a DOM render.
 */

/**
 * Cold-load bootstrap outcomes:
 * - `needs-refresh`: getSession returned no session but no refresh has been tried
 *   yet → attempt one token refresh before concluding the user is logged out.
 * - `no-session`: no session AND the refresh was terminally rejected → route to /login.
 * - `transient`: a getSession/me error, or a transient refresh failure → hold; retry.
 * - `loading`: still fetching (no data, no error yet), or a refresh just succeeded
 *   and the getSession refetch is in flight → hold.
 * - `ready`: session and profile both present → commit to the store.
 */
export type BootstrapOutcome = "needs-refresh" | "no-session" | "transient" | "loading" | "ready";

/**
 * Outcome of the one-shot bootstrap token refresh (mirrors RefreshOutcome), or
 * `null` when no refresh has been attempted yet this bootstrap.
 */
export type BootstrapRefreshOutcome = "success" | "terminal" | "transient" | null;

/**
 * Classify the bootstrap query state into a single outcome.
 *
 * A `null` session means the access token is missing or expired — which, while a
 * valid refresh token remains, is the normal steady state, not a logged-out one.
 * So the first `null` yields `needs-refresh` (attempt a refresh); only a refresh
 * that is TERMINALLY rejected (401/403) becomes `no-session` → /login. Any error
 * (query or transient refresh) is held so a validly-cookied user is never logged
 * out by a blip on app load.
 * @param args - The current bootstrap query state.
 * @param args.sessionData - getSession result: `null` = no session, `undefined` = loading, object = session.
 * @param args.sessionError - Whether the getSession query is in an error state.
 * @param args.meData - me (profile) result, `undefined` until loaded.
 * @param args.meError - Whether the me query is in an error state.
 * @param args.refreshOutcome - Result of the one-shot bootstrap refresh, or `null` if not yet attempted.
 * @returns The bootstrap outcome the effect should act on.
 */
export function classifyBootstrap(args: {
	sessionData: unknown;
	sessionError: boolean;
	meData: unknown;
	meError: boolean;
	refreshOutcome: BootstrapRefreshOutcome;
}): BootstrapOutcome {
	if (args.sessionData === null) {
		// Access token gone. Try one refresh before treating this as logged out.
		if (args.refreshOutcome === null) return "needs-refresh";
		if (args.refreshOutcome === "terminal") return "no-session";
		if (args.refreshOutcome === "transient") return "transient";
		// "success": the getSession refetch is in flight; hold until it returns.
		return "loading";
	}
	if (args.sessionError) return "transient";
	if (args.sessionData && args.meData) return "ready";
	if (args.meError) return "transient";
	return "loading";
}
